import { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';

/**
 * Custom hook for accessing the theme context
 * 
 * This hook provides a convenient way to access the theme state and toggle function
 * from any component in the application. It ensures the component is used within
 * a ThemeProvider by throwing an error if the context is undefined.
 * 
 * Returns:
 * - theme: The current theme ('light' or 'dark')
 * - toggleTheme: Function to switch between light and dark themes
 * 
 * Usage example:
 * ```jsx
 * const MyComponent = () => {
 *   const { theme, toggleTheme } = useTheme();
 *   
 *   return (
 *     <div>
 *       <p>Current theme: {theme}</p>
 *       <button onClick={toggleTheme}>Toggle Theme</button>
 *     </div>
 *   );
 * };
 * ```
 * 
 * @returns {Object} An object containing the theme state and toggle function
 * @throws {Error} If used outside of a ThemeProvider
 */
export const useTheme = () => {
  // Access the ThemeContext using React's useContext hook
  const context = useContext(ThemeContext);
  
  // Ensure the hook is used within a ThemeProvider
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}; 